// src/features/hospitalSetting/hospitalSettingSelectors.ts

import { RootState } from "@core/store"
import {
  HospitalSetting,
  BetaSettingsData,
  KakaoAlimtalkSettingsData,
  createDefaultBetaSettingsData,
  createDefaultDoctalkReservationSettingsData,
  normalizeBetaSettingsData,
  normalizeDoctalkReservationSettingsData,
  DoctalkReservationSettingsData,
  UninsuredDiscountSettingsData,
  createDefaultUninsuredDiscountSettings,
  normalizeUninsuredDiscountSettingsData,
} from "@/types/hospitalSetting"
import {
  createDefaultMemberGrantPermissionSettingsData,
  MEMBER_GRANT_PERMISSION_SETTING_NAME,
  MemberGrantPermissionSettingsData,
  normalizeMemberGrantPermissionSettingsData,
} from "@core/constants/memberGrantPermissionSettings"
import type { EncounterChartAutoCreateMode } from "@core/api/hospitalSettingApi"
import type { DailyClosingPatientSortOrder } from "@core/api/hospitalSettingApi"
import type { MsureEnvironment } from "@core/api/hospitalSettingApi"
import type { PatientReferrerBadgeIconKey } from "@core/api/hospitalSettingApi"
import type { PatientReferrerBadgeRule } from "@core/api/hospitalSettingApi"
import type { ReFirstVisitChangeMode } from "@core/api/hospitalSettingApi"
import { createSelector } from "@reduxjs/toolkit"

/**
 * HospitalSetting Selectors
 *
 * 다이나믹 설정 조회 시스템
 */

// 모든 설정 가져오기
export const hospitalSettingsSelector = (state: RootState) => state.hospitalSetting.settings

// 설정 로드 상태
export const hospitalSettingsLoadedSelector = (state: RootState) => state.hospitalSetting.loaded
export const hospitalSettingsLoadingSelector = (state: RootState) => state.hospitalSetting.loading
export const hospitalSettingsErrorSelector = (state: RootState) => state.hospitalSetting.error

// 특정 설정 가져오기 (없으면 null)
export const getSettingSelector =
  (settingName: string) =>
  (state: RootState): HospitalSetting | null => {
    return state.hospitalSetting.settings[settingName] || null
  }

// 특정 설정의 데이터만 가져오기 (타입 안전성)
export const getSettingDataSelector =
  <T = any>(settingName: string, defaultValue: T) =>
  (state: RootState): T => {
    const setting = state.hospitalSetting.settings[settingName]
    return setting ? (setting.hospitalSettingData as T) : defaultValue
  }

// 특정 설정의 특정 필드 가져오기
export const getSettingFieldSelector =
  <T = any>(settingName: string, fieldName: string, defaultValue: T) =>
  (state: RootState): T => {
    const setting = state.hospitalSetting.settings[settingName]
    if (setting && setting.hospitalSettingData && fieldName in setting.hospitalSettingData) {
      return setting.hospitalSettingData[fieldName] as T
    }
    return defaultValue
  }

/**
 * 특정 설정들을 위한 Selector (자주 사용되는 설정)
 */

// SMS 설정
export interface SmsSettingsData {
  smsReservedSameDay?: boolean
  smsReservedPreviousDay?: boolean
  smsReservedCurrentDay?: boolean
}

export const smsSettingsSelector = createSelector(
  [(state: RootState) => state.hospitalSetting.settings["smsSettings"]],
  (setting): SmsSettingsData => {
    if (setting && setting.hospitalSettingData) {
      return setting.hospitalSettingData as SmsSettingsData
    }
    return {
      smsReservedSameDay: false,
      smsReservedPreviousDay: false,
      smsReservedCurrentDay: false,
    }
  }
)

// 보안 설정
export interface SecuritySettingsData {
  passwordChangeInterval?: string
  autoLogoutDurationMinutes?: number
}

export const securitySettingsSelector = createSelector(
  [(state: RootState) => state.hospitalSetting.settings["securitySettings"]],
  (setting): SecuritySettingsData => {
    if (setting && setting.hospitalSettingData) {
      return setting.hospitalSettingData as SecuritySettingsData
    }
    return {
      passwordChangeInterval: "never",
      autoLogoutDurationMinutes: 60,
    }
  }
)

const parseMiscBoolean = (value: unknown, fallback: boolean) => {
  if (value === undefined || value === null) {
    return fallback
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase()
    return ["1", "true", "yes", "y", "on"].includes(normalized)
  }

  return value === true || value === 1
}

const parseMiscPositiveInteger = (value: unknown, fallback: number) => {
  const parsed = Number(value)

  if (!Number.isFinite(parsed)) {
    return fallback
  }

  return Math.max(1, Math.floor(parsed))
}

const parseEncounterChartAutoCreateMode = (value: unknown): EncounterChartAutoCreateMode => {
  if (value === "manual" || value === "auto" || value === "primaryDoctorOnly" || value === "assignedDoctorOnly") {
    return value
  }

  return "auto"
}

const parseReFirstVisitChangeMode = (value: unknown): ReFirstVisitChangeMode => {
  if (value === "default" || value === "over90Only") {
    return value
  }

  return "default"
}

const parseMsureEnvironment = (value: unknown): MsureEnvironment => {
  if (value === "dev" || value === "prod") {
    return value
  }

  return "prod"
}

const parseDailyClosingPatientSortOrder = (value: unknown): DailyClosingPatientSortOrder => {
  return value === "name" ? "name" : "time"
}

const parseReferrerBadgeIcon = (value: unknown): PatientReferrerBadgeIconKey => {
  if (
    value === "star" ||
    value === "dot" ||
    value === "triangle" ||
    value === "diamond" ||
    value === "square" ||
    value === "doubleCircle" ||
    value === "doubleDiamond" ||
    value === "asterisk" ||
    value === "custom"
  ) {
    return value
  }

  return "star"
}

const normalizeReferrerBadgeCustomSymbol = (value: unknown): string => {
  const trimmedValue = String(value ?? "").trim()
  if (!trimmedValue) {
    return ""
  }

  const [firstCharacter = ""] = Array.from(trimmedValue)
  if (!firstCharacter) {
    return ""
  }

  if (/\p{Extended_Pictographic}/u.test(firstCharacter)) {
    return ""
  }

  if (/[\p{L}\p{N}\p{Z}]/u.test(firstCharacter)) {
    return ""
  }

  return firstCharacter
}

const createDefaultReferrerBadgeRule = (): PatientReferrerBadgeRule => ({
  minCount: 1,
  maxCount: 5,
  icon: "star",
  customSymbol: "",
})

const normalizeReferrerBadgeRule = (value: unknown, fallbackRule?: PatientReferrerBadgeRule): PatientReferrerBadgeRule => {
  const baseRule = fallbackRule ?? createDefaultReferrerBadgeRule()
  const rawRule = typeof value === "object" && value !== null ? (value as PatientReferrerBadgeRule) : {}
  const minCount = parseMiscPositiveInteger(rawRule.minCount, parseMiscPositiveInteger(baseRule.minCount, 1))
  const maxCount = Math.max(minCount, parseMiscPositiveInteger(rawRule.maxCount, parseMiscPositiveInteger(baseRule.maxCount, 5)))
  const icon = parseReferrerBadgeIcon(rawRule.icon ?? baseRule.icon)

  return {
    minCount,
    maxCount,
    icon,
    customSymbol: normalizeReferrerBadgeCustomSymbol(rawRule.customSymbol ?? baseRule.customSymbol),
  }
}

// 여러가지 설정
export interface MiscSettingsData {
  reservationTimeUnit: number // 예약시간 단위 (분): 10, 15, 20, 30, 60
  renewalReservationPreferred?: boolean
  naverReservationTimeEnabled?: boolean
  prescriptionAutoAnalysisEnabled?: boolean
  reservationSlotMaxPatientsEnabled?: boolean // 예약시간별 최대 예약환자 수 제한 사용 여부
  reservationSlotMaxPatients?: number // 예약시간별 최대 예약환자 수
  silson24ExcludeTodayEncounters?: boolean // 실손24 검색에서 당일 진료 제외 여부
  autoMoveToTreatmentAfterChartRecord?: boolean // 환자 차트기록 후 자동으로 치료목록 이동 여부
  excludeSeniorHealthInsuranceAutoSurcharge?: boolean // 65세 이상 건강보험 자동가산 설정 제외 여부
  autoAddDiagnosisTechniqueFee?: boolean // 가이드 허용 시 변증기술료 자동 추가 여부
  encounterChartAutoCreateMode?: EncounterChartAutoCreateMode // 차트 자동 생성 방식
  reFirstVisitChangeMode?: ReFirstVisitChangeMode // 재초진 변경 규칙
  msureEnabled?: boolean // 실손보험조회 사용 여부
  msureEnvironment?: MsureEnvironment // 실손보험조회 대상 서버
  cidEnabled?: boolean // CID 연동 사용 여부
  cardTerminalEnabled?: boolean // 신용카드 단말기 연동 사용 여부
  referrerBadgeEnabled?: boolean
  referrerBadgeRules?: PatientReferrerBadgeRule[]
  dailyClosingPatientSortOrder?: DailyClosingPatientSortOrder
}

export const miscSettingsSelector = createSelector(
  [(state: RootState) => state.hospitalSetting.settings["miscSettings"]],
  (setting): MiscSettingsData => {
    if (setting && setting.hospitalSettingData) {
      const miscSettings = setting.hospitalSettingData as MiscSettingsData
      const encounterChartAutoCreateMode =
        miscSettings.encounterChartAutoCreateMode ??
        (setting.hospitalSettingData as MiscSettingsData & { encounter_chart_auto_create_mode?: unknown }).encounter_chart_auto_create_mode
      const reFirstVisitChangeMode =
        miscSettings.reFirstVisitChangeMode ??
        (setting.hospitalSettingData as MiscSettingsData & { re_first_visit_change_mode?: unknown }).re_first_visit_change_mode
      const normalizedReferrerBadgeRules =
        Array.isArray(miscSettings.referrerBadgeRules) && miscSettings.referrerBadgeRules.length > 0
          ? miscSettings.referrerBadgeRules.map(rule => normalizeReferrerBadgeRule(rule))
          : [createDefaultReferrerBadgeRule()]

      return {
        reservationTimeUnit: parseMiscPositiveInteger(miscSettings.reservationTimeUnit, 10),
        renewalReservationPreferred: parseMiscBoolean(miscSettings.renewalReservationPreferred, false),
        naverReservationTimeEnabled: parseMiscBoolean(miscSettings.naverReservationTimeEnabled, false),
        prescriptionAutoAnalysisEnabled: parseMiscBoolean(miscSettings.prescriptionAutoAnalysisEnabled, true),
        reservationSlotMaxPatientsEnabled: parseMiscBoolean(miscSettings.reservationSlotMaxPatientsEnabled, false),
        reservationSlotMaxPatients: parseMiscPositiveInteger(miscSettings.reservationSlotMaxPatients, 4),
        silson24ExcludeTodayEncounters: parseMiscBoolean(miscSettings.silson24ExcludeTodayEncounters, false),
        autoMoveToTreatmentAfterChartRecord: parseMiscBoolean(miscSettings.autoMoveToTreatmentAfterChartRecord, true),
        excludeSeniorHealthInsuranceAutoSurcharge: parseMiscBoolean(miscSettings.excludeSeniorHealthInsuranceAutoSurcharge, false),
        autoAddDiagnosisTechniqueFee: parseMiscBoolean(miscSettings.autoAddDiagnosisTechniqueFee, false),
        encounterChartAutoCreateMode: parseEncounterChartAutoCreateMode(encounterChartAutoCreateMode),
        reFirstVisitChangeMode: parseReFirstVisitChangeMode(reFirstVisitChangeMode),
        msureEnabled: parseMiscBoolean(miscSettings.msureEnabled, false),
        msureEnvironment: parseMsureEnvironment(
          miscSettings.msureEnvironment ?? (setting.hospitalSettingData as MiscSettingsData & { msure_environment?: unknown }).msure_environment
        ),
        cidEnabled: true,
        cardTerminalEnabled: parseMiscBoolean(
          miscSettings.cardTerminalEnabled ??
            (setting.hospitalSettingData as MiscSettingsData & { card_terminal_enabled?: unknown }).card_terminal_enabled,
          false
        ),
        referrerBadgeEnabled: parseMiscBoolean(miscSettings.referrerBadgeEnabled, false),
        referrerBadgeRules: normalizedReferrerBadgeRules,
        dailyClosingPatientSortOrder: parseDailyClosingPatientSortOrder(miscSettings.dailyClosingPatientSortOrder),
      }
    }

    const defaultReferrerBadgeRule = createDefaultReferrerBadgeRule()

    return {
      reservationTimeUnit: 10, // 기본값: 10분
      renewalReservationPreferred: false,
      naverReservationTimeEnabled: false,
      prescriptionAutoAnalysisEnabled: true,
      reservationSlotMaxPatientsEnabled: false,
      reservationSlotMaxPatients: 4,
      silson24ExcludeTodayEncounters: false,
      autoMoveToTreatmentAfterChartRecord: true,
      excludeSeniorHealthInsuranceAutoSurcharge: false,
      autoAddDiagnosisTechniqueFee: false,
      encounterChartAutoCreateMode: "auto",
      reFirstVisitChangeMode: "default",
      msureEnabled: false,
      msureEnvironment: "prod",
      cidEnabled: true,
      cardTerminalEnabled: false,
      referrerBadgeEnabled: false,
      referrerBadgeRules: [defaultReferrerBadgeRule],
      dailyClosingPatientSortOrder: "time",
    }
  }
)

export const uninsuredDiscountSettingsSelector = createSelector(
  [(state: RootState) => state.hospitalSetting.settings["uninsuredDiscountSettings"]],
  (setting): UninsuredDiscountSettingsData => {
    if (setting && setting.hospitalSettingData) {
      return normalizeUninsuredDiscountSettingsData(setting.hospitalSettingData)
    }
    return createDefaultUninsuredDiscountSettings()
  }
)

export const betaSettingsSelector = createSelector(
  [(state: RootState) => state.hospitalSetting.settings["betaSettings"]],
  (setting): BetaSettingsData => {
    if (setting && setting.hospitalSettingData) {
      return normalizeBetaSettingsData(setting.hospitalSettingData)
    }

    return createDefaultBetaSettingsData()
  }
)

export const memberGrantPermissionSettingsSelector = createSelector(
  [(state: RootState) => state.hospitalSetting.settings[MEMBER_GRANT_PERMISSION_SETTING_NAME]],
  (setting): MemberGrantPermissionSettingsData => {
    if (setting && setting.hospitalSettingData) {
      return normalizeMemberGrantPermissionSettingsData(setting.hospitalSettingData)
    }

    return createDefaultMemberGrantPermissionSettingsData()
  }
)

export const effectiveCrmEnabledSelector = createSelector([betaSettingsSelector], betaSettings => betaSettings.betaEnabled && betaSettings.crmEnabled)

export const effectivePharmaOneButtonEnabledSelector = createSelector(
  [betaSettingsSelector],
  betaSettings => betaSettings.betaEnabled && betaSettings.pharmaOneButtonEnabled
)

export const effectiveNhisChupyakEnabledSelector = createSelector([betaSettingsSelector], betaSettings => {
  return betaSettings.nhisChupyakEnabled
})

export const effectiveDoctalkEnabledSelector = createSelector([betaSettingsSelector], betaSettings => {
  return true
})

export const effectiveAiAnalysisEnabledSelector = createSelector([betaSettingsSelector], betaSettings => {
  return betaSettings.betaEnabled && betaSettings.aiAnalysisEnabled
})

export const effectiveGptEnabledSelector = createSelector([betaSettingsSelector], betaSettings => {
  return betaSettings.betaEnabled && betaSettings.gptEnabled
})

export const effectiveCommandBotEnabledSelector = createSelector([betaSettingsSelector], betaSettings => {
  return betaSettings.betaEnabled && betaSettings.commandBotEnabled
})

export const cardTerminalFeatureVisibleSelector = createSelector([betaSettingsSelector], betaSettings => {
  return betaSettings.betaEnabled && betaSettings.cardTerminalEnabled
})

export const effectiveCardTerminalEnabledSelector = createSelector(
  [cardTerminalFeatureVisibleSelector, miscSettingsSelector],
  (cardTerminalFeatureVisible, miscSettings) => {
    return cardTerminalFeatureVisible && miscSettings.cardTerminalEnabled === true
  }
)

export const effectiveMsureSilsonEnabledSelector = createSelector([betaSettingsSelector], betaSettings => {
  return true
})

export const effectivePaymentBalanceFeatureEnabledSelector = createSelector([betaSettingsSelector], betaSettings => {
  return true
})

export const effectiveServerQualificationEnabledSelector = createSelector([betaSettingsSelector], (): boolean => true)

export const effectiveServerHiraEnabledSelector = createSelector([betaSettingsSelector], betaSettings => {
  return betaSettings.serverHiraEnabled
})

export const effectiveMedicalRecordExternalNoteEnabledSelector = createSelector([betaSettingsSelector], betaSettings => {
  return betaSettings.betaEnabled && betaSettings.medicalRecordExternalNoteEnabled
})

export const effectiveDigitalSignatureEnabledSelector = createSelector([betaSettingsSelector], (): boolean => true)

export const effectiveMemberAvatarEnabledSelector = createSelector([betaSettingsSelector], betaSettings => {
  return true
})

export const effectivePenChartEnabledSelector = createSelector([betaSettingsSelector], betaSettings => {
  return betaSettings.betaEnabled && betaSettings.penChartEnabled
})

export const effectivePatientAnalysisEnabledSelector = createSelector([betaSettingsSelector], betaSettings => {
  return true
})

export const effectivePackageUsageEnabledSelector = createSelector([betaSettingsSelector], betaSettings => {
  return true
})

export const effectivePrepaymentUsageEnabledSelector = createSelector([betaSettingsSelector], betaSettings => {
  return betaSettings.betaEnabled && betaSettings.prepaymentUsageEnabled
})

export const effectiveDiagnosisPatternAssistantEnabledSelector = createSelector([betaSettingsSelector], betaSettings => {
  return betaSettings.betaEnabled && betaSettings.diagnosisPatternAssistantEnabled
})

export const effectiveServerQualificationRouteAccessSelector = createSelector(
  [hospitalSettingsLoadedSelector, hospitalSettingsErrorSelector, betaSettingsSelector],
  (): boolean => true
)

export const effectiveDigitalSignatureRouteAccessSelector = createSelector(
  [hospitalSettingsLoadedSelector, hospitalSettingsErrorSelector, betaSettingsSelector],
  (): boolean => true
)

// 채팅 설정
export interface ChatSettingsData {
  chatEnabled: boolean
  chatRetentionMonths: number // 채팅내역 저장기간 (개월): 1, 3, 6, 12, 36, 60, 120
  chatHistoryPublicEnabled?: boolean
  chatHistoryPublicDays?: number
}

export const chatSettingsSelector = createSelector(
  [(state: RootState) => state.hospitalSetting.settings["chatSettings"]],
  (setting): ChatSettingsData => {
    if (setting && setting.hospitalSettingData) {
      return setting.hospitalSettingData as ChatSettingsData
    }
    return {
      chatEnabled: true, // 기본값: 채팅 사용
      chatRetentionMonths: 12, // 기본값: 12개월
      chatHistoryPublicEnabled: false,
      chatHistoryPublicDays: 7,
    }
  }
)

export const kakaoAlimtalkSettingsSelector = createSelector(
  [(state: RootState) => state.hospitalSetting.settings["kakaoAlimtalkSettings"]],
  (setting): KakaoAlimtalkSettingsData => {
    const favoriteTemplateUuids = setting?.hospitalSettingData?.favoriteTemplateUuids
    const paymentAutoSend = setting?.hospitalSettingData?.paymentAutoSend
    const normalizeRule = (rule: any) => ({
      enabled: rule?.enabled === true,
      firstVisit: rule?.firstVisit === true,
      returnVisit: rule?.returnVisit === true,
      templateUuid: typeof rule?.templateUuid === "string" ? rule.templateUuid : "",
    })
    return {
      enabled: setting?.hospitalSettingData?.enabled === true,
      favoriteTemplateUuids: Array.isArray(favoriteTemplateUuids)
        ? favoriteTemplateUuids.filter((value): value is string => typeof value === "string" && Boolean(value.trim()))
        : [],
      paymentAutoSend: {
        enabled: paymentAutoSend?.enabled === true,
        chuna: normalizeRule(paymentAutoSend?.chuna),
        package: normalizeRule(paymentAutoSend?.package),
        custom: Array.isArray(paymentAutoSend?.custom)
          ? paymentAutoSend.custom.map((rule: any, index: number) => ({
              ...normalizeRule(rule),
              ruleUuid: typeof rule?.ruleUuid === "string" && rule.ruleUuid ? rule.ruleUuid : `legacy-${index}`,
              uninsuredName: typeof rule?.uninsuredName === "string" ? rule.uninsuredName : "",
            }))
          : [],
      },
    }
  }
)

export const doctalkReservationSettingsSelector = createSelector(
  [(state: RootState) => state.hospitalSetting.settings["doctalkReservationSettings"]],
  (setting): DoctalkReservationSettingsData => {
    if (setting && setting.hospitalSettingData) {
      return normalizeDoctalkReservationSettingsData(setting.hospitalSettingData)
    }

    return createDefaultDoctalkReservationSettingsData()
  }
)
